import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { Provider } from "react-redux";
import { appStore } from "./store/appStore.ts";
import i18n from "./common/i18n.ts";
import { I18nextProvider } from "react-i18next";
import { Loader } from "@carel-tools/carel-tools-react-component";
import { createTheme } from '@mui/material/styles';
import CssBaseline from "@mui/material/CssBaseline";
import ThemeProvider from "@mui/system/ThemeProvider";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { AppRoutes } from './common/appRoutes.ts';
import { Redirect } from './component/page/redirect/Redirect.tsx';
import * as Sentry from '@sentry/react';
import { Settings } from './common/settings.ts';
import { signInWhenTokenExpires } from './common/userManager.ts';


const LandingPageView = lazy(() => import('./component/page/landing-page/LandingPage.tsx').then(module => ({ default: module.LandingPage })));
const LoginView = lazy(() => import('./component/page/login/Login.tsx').then(module => ({ default: module.Login })));
const RegisterCompanyView = lazy(() => import('./component/page/register-company/RegisterCompany.tsx').then(module => ({ default: module.RegisterCompany })));
const RootView = lazy(() => import('./component/page/root/Root.tsx').then(module => ({ default: module.Root })));
const HomeView = lazy(() => import('./component/page/home/Home.tsx').then(module => ({ default: module.Home })));
const ApplicationListView = lazy(() => import('./component/page/application-list/ApplicationList.tsx').then(module => ({ default: module.ApplicationList })));
const CreditsView = lazy(() => import('./component/page/credits/Credits.tsx').then(module => ({ default: module.Credits })));
const UserActivityLogView = lazy(() => import('./component/page/user-activity-log/UserActivityLog.tsx').then(module => ({ default: module.UserActivityLog })));
const TenantListView = lazy(() => import('./component/page/tenant-list/TenantList.tsx').then(module => ({ default: module.TenantList })));
const OrderListView = lazy(() => import('./component/page/order-list/OrderList.tsx').then(module => ({ default: module.OrderList })));
const OrderView = lazy(() => import('./component/page/order/Order.tsx').then(module => ({ default: module.Order })));
const UserListView = lazy(() => import('./component/page/user-list/UserList.tsx').then(module => ({ default: module.UserList })));
const ConsumptionsView = lazy(() => import('./component/page/consumptions/Consumptions.tsx').then(module => ({ default: module.Consumptions })));
const SingleSignOnView = lazy(() => import('./component/page/single-sign-on/SingleSignOn.tsx').then(module => ({ default: module.SingleSignOn })));
const SubscriptionsView = lazy(() => import('./component/page/subscriptions/Subscriptions.tsx').then(module => ({ default: module.Subscriptions })));
const ActivationRenewalView = lazy(() => import('./component/page/activation-renewal/ActivationRenewal.tsx').then(module => ({ default: module.ActivationRenewal })));
const PublicRootView = lazy(() => import('./component/page/public-root/PublicRoot.tsx').then(module => ({ default: module.PublicRoot })));
const StoneCopilotFrequentlyAskedQuestionsView = lazy(() => import('./component/page/public-stone-copilot/frequently-asked-questions/StoneCopilotFrequentlyAskedQuestions.tsx').then(module => ({ default: module.StoneCopilotFrequentlyAskedQuestions })));

const router = createBrowserRouter([
    {
        Component: PublicRootView,
        children:
            [
                {
                    path: AppRoutes.LandingPage,
                    Component: LandingPageView,
                },
                {
                    path: AppRoutes.Login,
                    Component: LoginView
                },
                {
                    path: AppRoutes.Registration,
                    Component: RegisterCompanyView
                },
                {
                    path: AppRoutes.Redirect,
                    Component: Redirect,
                },
                {
                    path: AppRoutes.StoneCopilotFrequentlyAskedQuestions,
                    Component: StoneCopilotFrequentlyAskedQuestionsView,
                }
            ]
    },
    {
        path: AppRoutes.Home,
        Component: RootView,
        children:
            [
                {
                    index: true,
                    Component: HomeView
                },
                {
                    path: AppRoutes.ApplicationList,
                    Component: ApplicationListView
                },
                {
                    path: AppRoutes.Credits,
                    Component: CreditsView
                },
                {
                    path: AppRoutes.UserActivityLog,
                    Component: UserActivityLogView
                },
                {
                    path: AppRoutes.TenantList,
                    Component: TenantListView
                },
                {
                    path: AppRoutes.OrderList,
                    Component: OrderListView
                },
                {
                    path: AppRoutes.Order,
                    Component: OrderView
                },
                {
                    path: AppRoutes.UserList,
                    Component: UserListView
                },
                {
                    path: AppRoutes.Consumptions,
                    Component: ConsumptionsView
                },
                {
                    path: AppRoutes.SingleSignOn,
                    Component: SingleSignOnView
                },
                {
                    path: AppRoutes.Subscriptions,
                    Component: SubscriptionsView
                },
                {
                    path: AppRoutes.ActivationRenewal,
                    Component: ActivationRenewalView
                }
            ]
    },
    {
        path: "*",
        element: <Navigate to={AppRoutes.LandingPage} replace />
    }
]);

const theme = createTheme({
    palette: {
        primary: {
            main: '#FE0000'
        },
        secondary: {
            main: '#FE0000'
        },
    },
});

const container = document.getElementById('root');

const root = createRoot(container!, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Caught error', error, errorInfo.componentStack);
    }),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Recoverable error', error, errorInfo.componentStack);
    }),
});

signInWhenTokenExpires();

Sentry.init({
    dsn: Settings.getSentryDsn(),
    environment: Settings.getEnvironment(),
    debug: false,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets:[
        /^https:\/\/green-smoke-074fdee03.7.azurestaticapps.net/,
        /^https:\/\/servicesmanager-qa.corp.carel.com/,
        /^https:\/\/servicesmanager.corp.carel.com/,
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(sentryEvent, _) {
        if (sentryEvent.user) {
            sentryEvent.user.email = undefined;
            sentryEvent.user.ipAddress = undefined;
            sentryEvent.user.username = undefined;
        }
        return sentryEvent;
    },
    beforeSendTransaction(sentryTransaction, _) {
        if (sentryTransaction.user) {
            sentryTransaction.user.email = undefined;
            sentryTransaction.user.ipAddress = undefined;
            sentryTransaction.user.username = undefined;
        }
        return sentryTransaction;
    }
});

root.render(<React.StrictMode>
    <ThemeProvider theme={theme}>
        <Provider store={appStore}>
            <Suspense fallback={<Loader />}>
                <I18nextProvider i18n={i18n}>
                    <CssBaseline />
                    <RouterProvider router={router} />
                </I18nextProvider>
            </Suspense>
        </Provider>
    </ThemeProvider>
</React.StrictMode>);
